import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Rating } from "@smastrom/react-rating";
import "@smastrom/react-rating/style.css";
import Button from "../../controls/Button";
import { ReviewService } from "../../services";
import { usePersistStore, useStore } from "../../store";
import SellerInfo from "../../components/SellerInfo";

function Review() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage, setIsLoading } = useStore(
    (s: any) => s
  );

  const [ad, setAd] = useState<any>(null);
  const [sellerAggregate, setSellerAggregate] = useState<{
    avg: number;
    count: number;
  } | null>(null);
  const [sellerReview, setSellerReview] = useState<any>(null);
  const [platformReview, setPlatformReview] = useState<any>(null);
  const [sellerRating, setSellerRating] = useState<number>(0);
  const [sellerText, setSellerText] = useState<string>("");
  const [platformRating, setPlatformRating] = useState<number>(0);
  const [platformText, setPlatformText] = useState<string>("");
  const [isReady, setIsReady] = useState(false);

  const reload = useCallback(async () => {
    if (!token || !adId) return;
    setIsReady(false);
    const result = await ReviewService.getForAd(token, adId);
    if (result.error) {
      setErrorMessage(result.error);
      setIsReady(true);
      return;
    }
    setAd(result.data?.ad);
    setSellerReview(result.data?.sellerReview);
    setPlatformReview(result.data?.platformReview);

    // Same seller aggregate the artwork details page shows, so the reviewer
    // sees the seller's existing rating while composing their own.
    const sellerId = result.data?.ad?.userId?._id;
    if (sellerId) {
      const agg = await ReviewService.getSellerAggregate(sellerId);
      if (!agg?.error) setSellerAggregate(agg?.data || null);
    }

    setIsReady(true);
  }, [token, adId, setErrorMessage]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submitSeller = async () => {
    if (!adId) return;
    if (!sellerRating || sellerRating < 1) {
      setErrorMessage("Please select a rating for the seller.");
      return;
    }
    if (!sellerText.trim()) {
      setErrorMessage("Please write a review for the seller.");
      return;
    }
    setIsLoading(true);
    const result = await ReviewService.submitSellerReview(token, {
      adId,
      rating: sellerRating,
      text: sellerText.trim(),
    });
    setIsLoading(false);
    if (result.error) {
      setErrorMessage(result.error);
      return;
    }
    setSuccessMessage("Thanks — your seller review has been submitted for moderation.");
    reload();
  };

  const submitPlatform = async () => {
    if (!adId) return;
    if (!platformRating || platformRating < 1) {
      setErrorMessage("Please select a rating for the platform.");
      return;
    }
    if (!platformText.trim()) {
      setErrorMessage("Please write a review for the platform.");
      return;
    }
    setIsLoading(true);
    const result = await ReviewService.submitPlatformReview(token, {
      adId,
      rating: platformRating,
      text: platformText.trim(),
    });
    setIsLoading(false);
    if (result.error) {
      setErrorMessage(result.error);
      return;
    }
    setSuccessMessage("Thanks — your platform review has been saved.");
    reload();
  };

  if (!isReady) {
    return (
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4 px-4">
              <p className="m-0">Loading…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4 px-4">
              <h3>Review unavailable</h3>
              <p>We couldn't load this purchase for review.</p>
              <Button
                title="Back to dashboard"
                onClick={() => navigate("/dashboard")}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const seller = ad?.userId;

  return (
    <div className="bg-pink p-4">
      <div className="row">
        <div className="col-12">
          <div className="chartCard py-4">
            <div className="tableCardHeader px-4">
              <div className="tableCardHeader__titleBlock">
                <h2 className="tableCardHeader__title m-0">Share your review</h2>
                <p className="tableCardHeader__subtitle m-0">
                  Rate your seller and tell us how the platform worked for you.
                </p>
              </div>
            </div>

            <div className="reviewPage__body">
              <div className="reviewPage__hero">
                <Link
                  to={`/details/${ad?._id}`}
                  className="reviewPage__thumbLink"
                  aria-label={`Open ${ad?.title}`}
                >
                  <img
                    src={ad?.images?.[0] || "/assets/imgs/crawl.png"}
                    alt={ad?.title}
                    className="reviewPage__thumb"
                  />
                </Link>
                <div className="reviewPage__heroMeta">
                  <Link
                    to={`/details/${ad?._id}`}
                    className="reviewPage__titleLink"
                  >
                    <h3 className="reviewPage__title">{ad?.title}</h3>
                  </Link>
                  <SellerInfo
                    seller={seller}
                    aggregate={sellerAggregate}
                    size="small"
                  />
                </div>
              </div>

              {/* Seller review */}
              <section className="reviewPage__section">
                <div className="reviewPage__sectionHeader">
                  <h4 className="reviewPage__sectionTitle">Review the seller</h4>
                  <p className="reviewPage__sectionSub">
                    Your rating and comments will appear on the seller's
                    profile after our team reviews them.
                  </p>
                </div>
                {sellerReview ? (
                  <div className="reviewPage__submitted">
                    <p className="reviewPage__submittedLabel">
                      <i className="bi bi-check-circle-fill" aria-hidden="true" />{" "}
                      You've already submitted this review.
                    </p>
                    <div className="reviewPage__submittedRating">
                      <Rating
                        style={{ maxWidth: 120 }}
                        value={sellerReview.rating}
                        readOnly
                      />
                      <span>{sellerReview.rating} / 5</span>
                    </div>
                    <p className="reviewPage__submittedText">
                      {sellerReview.text}
                    </p>
                  </div>
                ) : (
                  <div className="reviewPage__form">
                    <Rating
                      style={{ maxWidth: 180 }}
                      value={sellerRating}
                      onChange={setSellerRating}
                    />
                    <div className="formMain">
                      <textarea
                        className="form-control reviewPage__textarea"
                        rows={3}
                        placeholder="Tell buyers about your experience with this seller."
                        value={sellerText}
                        maxLength={2000}
                        onChange={(e) => setSellerText(e.target.value)}
                      />
                    </div>
                    <div className="reviewPage__actions">
                      <Button
                        title="Submit seller review"
                        onClick={submitSeller}
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* Platform review */}
              <section className="reviewPage__section">
                <div className="reviewPage__sectionHeader">
                  <h4 className="reviewPage__sectionTitle">Review our platform</h4>
                  <p className="reviewPage__sectionSub">
                    How was your overall experience buying on Boll-e&trade;? This
                    feedback goes only to our team.
                  </p>
                </div>
                {platformReview ? (
                  <div className="reviewPage__submitted">
                    <p className="reviewPage__submittedLabel">
                      <i className="bi bi-check-circle-fill" aria-hidden="true" />{" "}
                      You've already submitted this review.
                    </p>
                    <div className="reviewPage__submittedRating">
                      <Rating
                        style={{ maxWidth: 120 }}
                        value={platformReview.rating}
                        readOnly
                      />
                      <span>{platformReview.rating} / 5</span>
                    </div>
                    <p className="reviewPage__submittedText">
                      {platformReview.text}
                    </p>
                  </div>
                ) : (
                  <div className="reviewPage__form">
                    <Rating
                      style={{ maxWidth: 180 }}
                      value={platformRating}
                      onChange={setPlatformRating}
                    />
                    <div className="formMain">
                      <textarea
                        className="form-control reviewPage__textarea"
                        rows={3}
                        placeholder="Tell us what worked and what didn't."
                        value={platformText}
                        maxLength={2000}
                        onChange={(e) => setPlatformText(e.target.value)}
                      />
                    </div>
                    <div className="reviewPage__actions">
                      <Button
                        title="Submit platform review"
                        onClick={submitPlatform}
                      />
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Review;
